import { ANALYTICS_CONFIG } from "../config/analytics";
import { isKioskMode } from "../config/kiosk";

const PRIMARY_MEASUREMENT_ID = ANALYTICS_CONFIG.measurementId;
const SECONDARY_MEASUREMENT_ID = ANALYTICS_CONFIG.secondaryMeasurementId;
const getMeasurementIds = () => [PRIMARY_MEASUREMENT_ID, SECONDARY_MEASUREMENT_ID].filter(Boolean);

// Which UI a session is seeing — lets every event below be split "kiosk" vs
// "standard" in GA4 without touching each individual tracking call.
const getMenuMode = () => (isKioskMode() ? "kiosk" : "standard");

const withDebugLog = (type, payload) => {
  if (ANALYTICS_CONFIG.enableDebugLogging) {
    console.log(`📊 Analytics: ${type}`, payload);
  }
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const safeString = (value) => (value == null ? "" : String(value));

const buildCartItemsPayload = (items = []) =>
  (Array.isArray(items) ? items : []).slice(0, 25).map((item) => ({
    item_id: safeString(item?.skuCode || item?.itemId || item?.productId || item?.id),
    item_name: safeString(item?.productName || item?.shortName || item?.name),
    item_category: safeString(item?.categoryName || item?.subCategoryName),
    item_variant: [
      item?.variantTitle,
      ...(Array.isArray(item?.selectedOptions) ? item.selectedOptions : []).map(
        (option) => option?.title || option?.name
      ),
    ]
      .filter(Boolean)
      .join(" | "),
    price: safeNumber(item?.unitPrice),
    quantity: safeNumber(item?.quantity, 1),
  }));

const buildSingleItemPayload = (item) => buildCartItemsPayload(item ? [item] : []);

const buildCommonCartParams = ({
  cafeId,
  cafeName,
  source,
  itemCount,
  lineCount,
  subtotal,
  taxTotal,
  grandTotal,
  hasTableNumber,
  hasPhoneNumber,
}) => ({
  cafe_id: safeString(cafeId),
  cafe_name: safeString(cafeName),
  source: safeString(source),
  cart_item_count: safeNumber(itemCount),
  cart_line_count: safeNumber(lineCount),
  subtotal_value: safeNumber(subtotal),
  tax_value: safeNumber(taxTotal),
  total_value: safeNumber(grandTotal),
  has_table_number: Boolean(hasTableNumber),
  has_phone_number: Boolean(hasPhoneNumber),
});

export const initGA = () => {
  if (typeof window === "undefined" || !window.gtag) return;

  if (PRIMARY_MEASUREMENT_ID) {
    window.gtag("config", PRIMARY_MEASUREMENT_ID, {
      page_title: "BTC Cafe Menu",
      custom_map: { custom_parameter_1: "cafe_selection" },
    });
  }

  if (SECONDARY_MEASUREMENT_ID) {
    window.gtag("config", SECONDARY_MEASUREMENT_ID, {
      page_title: "BTC Cafe Menu",
      custom_map: { custom_parameter_1: "cafe_selection" },
      send_page_view: false,
    });
  }

  withDebugLog("Init", { measurementIds: getMeasurementIds() });
};

const sendEvent = (eventName, eventParams = {}, destination = PRIMARY_MEASUREMENT_ID) => {
  if (!ANALYTICS_CONFIG.trackingEnabled) return;
  if (typeof window === "undefined" || !window.gtag || !destination) return;

  window.gtag("event", eventName, {
    event_category: "BTC_Cafe_Menu",
    event_label: eventParams.label || "",
    value: eventParams.value || 0,
    send_to: destination,
    ...eventParams,
    menu_mode: getMenuMode(),
  });
};

export const trackPageView = (pageName, additionalParams = {}) => {
  sendEvent(
    "page_view",
    {
      page_title: pageName,
      page_location: typeof window !== "undefined" ? window.location.href : "",
      page_path: typeof window !== "undefined" ? window.location.pathname : "",
      ...additionalParams,
    },
    PRIMARY_MEASUREMENT_ID
  );

  withDebugLog("Page View", {
    page: pageName,
    url: typeof window !== "undefined" ? window.location.href : "N/A",
    ...additionalParams,
  });
};

export const trackEvent = (eventName, eventParams = {}) => {
  sendEvent(eventName, eventParams, PRIMARY_MEASUREMENT_ID);
  withDebugLog(`Event ${eventName}`, eventParams);
};

const trackCartEvent = (eventName, eventParams = {}) => {
  sendEvent(eventName, eventParams, SECONDARY_MEASUREMENT_ID || PRIMARY_MEASUREMENT_ID);
  withDebugLog(`Cart Event ${eventName}`, eventParams);
};

export const trackBannerClick = (cafeId, cafeName, bannerInfo = {}) => {
  trackEvent("banner_click", {
    event_category: "User_Interaction",
    cafe_id: cafeId,
    cafe_name: cafeName,
    banner_id: bannerInfo.id || "",
    banner_name: bannerInfo.name || "",
    label: `Banner clicked: ${bannerInfo.name || bannerInfo.id || "unknown"}`,
  });
};

export const trackProductClick = (cafeId, cafeName, productInfo = {}) => {
  trackEvent("product_click", {
    event_category: "Menu_Interaction",
    cafe_id: cafeId,
    cafe_name: cafeName,
    product_id: productInfo.id || "",
    product_name: productInfo.name || "",
    product_category: productInfo.category || "",
    label: `Product clicked: ${productInfo.name || productInfo.id || "unknown"}`,
  });
};

export const trackCategoryClick = (cafeId, cafeName, categoryInfo = {}) => {
  trackEvent("category_click", {
    event_category: "Menu_Interaction",
    cafe_id: cafeId,
    cafe_name: cafeName,
    category_id: categoryInfo.id || "",
    category_name: categoryInfo.name || "",
    label: `Category clicked: ${categoryInfo.name || categoryInfo.id || "unknown"}`,
  });
};

export const trackSearchUsage = (cafeId, cafeName, searchTerm) => {
  trackEvent("search_usage", {
    event_category: "User_Interaction",
    cafe_id: cafeId,
    cafe_name: cafeName,
    search_term: searchTerm || "",
    label: `Search: ${searchTerm || "empty"}`,
  });
};

export const trackMenuView = (cafeId, cafeName) => {
  trackEvent("menu_viewed", {
    event_category: "Menu_Interaction",
    cafe_id: cafeId,
    cafe_name: cafeName,
    label: `Menu viewed for: ${cafeName}`,
  });
};

export const trackEngagementTime = (startTime, pageName) => {
  const endTime = Date.now();
  const timeSpent = Math.round((endTime - startTime) / 1000);

  trackEvent("engagement_time", {
    event_category: "User_Engagement",
    value: timeSpent,
    page_name: pageName,
    label: `${timeSpent}s on ${pageName}`,
  });
};

export const trackCartFeatureAvailability = ({
  cafeId,
  cafeName,
  isCartEnabled,
  source = "menu_load",
}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_feature_available, {
    event_category: "Cart_Flow",
    cafe_id: safeString(cafeId),
    cafe_name: safeString(cafeName),
    source,
    cart_enabled: Boolean(isCartEnabled),
    label: `Cart feature ${isCartEnabled ? "enabled" : "disabled"} for ${cafeId || cafeName || "unknown"}`,
  });
};

export const trackCartViewed = (params = {}) => {
  trackCartEvent("view_cart", {
    event_category: "Cart_Flow",
    currency: "INR",
    value: safeNumber(params.grandTotal),
    ...buildCommonCartParams(params),
    items: buildCartItemsPayload(params.items),
    label: `Cart opened from ${params.source || "unknown"}`,
  });

  trackCartEvent(ANALYTICS_CONFIG.events.cart_opened, {
    event_category: "Cart_Flow",
    ...buildCommonCartParams(params),
    label: `Cart opened from ${params.source || "unknown"}`,
  });
};

export const trackCartClosed = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_closed, {
    event_category: "Cart_Flow",
    ...buildCommonCartParams(params),
    label: `Cart closed from ${params.source || "unknown"}`,
  });
};

export const trackCartStepViewed = ({ stepName, ...params }) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_step_viewed, {
    event_category: "Cart_Flow",
    step_name: safeString(stepName),
    ...buildCommonCartParams(params),
    label: `Cart step viewed: ${stepName}`,
  });
};

export const trackCartTableCompleted = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_table_completed, {
    event_category: "Cart_Flow",
    table_number_entered: Boolean(params.hasTableNumber),
    ...buildCommonCartParams(params),
    label: "Table number completed",
  });
};

export const trackCartPhoneCompleted = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_phone_completed, {
    event_category: "Cart_Flow",
    phone_number_added: Boolean(params.hasPhoneNumber),
    ...buildCommonCartParams(params),
    label: "Phone number submitted",
  });
};

export const trackCartPhoneCaptured = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_phone_captured, {
    event_category: "Cart_Flow",
    ...buildCommonCartParams({
      ...params,
      hasPhoneNumber: true,
    }),
    label: "Phone number captured",
  });
};

export const trackCartPhoneSkipped = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_phone_skipped, {
    event_category: "Cart_Flow",
    ...buildCommonCartParams(params),
    label: "Phone number skipped",
  });
};

export const trackCartSummaryViewed = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_summary_viewed, {
    event_category: "Cart_Flow",
    currency: "INR",
    value: safeNumber(params.grandTotal),
    ...buildCommonCartParams(params),
    items: buildCartItemsPayload(params.items),
    label: "Order summary viewed",
  });
};

export const trackCartSummaryEditClicked = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_summary_edit_clicked, {
    event_category: "Cart_Flow",
    ...buildCommonCartParams(params),
    label: "Edit order clicked from summary",
  });
};

export const trackCartOrderDone = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_order_done, {
    event_category: "Cart_Flow",
    currency: "INR",
    value: safeNumber(params.grandTotal),
    ...buildCommonCartParams(params),
    items: buildCartItemsPayload(params.items),
    label: "Order handed over to barista",
  });
};

export const trackCartItemAdded = (params = {}) => {
  trackCartEvent("add_to_cart", {
    event_category: "Cart_Flow",
    currency: "INR",
    value: safeNumber(params.item?.unitPrice) * safeNumber(params.quantity, 1),
    ...buildCommonCartParams(params),
    add_source: safeString(params.addSource),
    has_customization: Boolean(params.hasCustomization),
    quantity_added: safeNumber(params.quantity, 1),
    items: buildSingleItemPayload({
      ...params.item,
      quantity: safeNumber(params.quantity, 1),
    }),
    label: `Added ${params.item?.productName || params.item?.name || "item"} to cart`,
  });
};

export const trackCartItemQuantityChanged = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_item_quantity_changed, {
    event_category: "Cart_Flow",
    action: safeString(params.action),
    delta: safeNumber(params.delta),
    resulting_quantity: safeNumber(params.resultingQuantity),
    has_customization: Boolean(params.hasCustomization),
    ...buildCommonCartParams(params),
    items: buildSingleItemPayload({
      ...params.item,
      quantity: safeNumber(params.resultingQuantity, 1),
    }),
    label: `${params.action || "changed"} quantity for ${params.item?.productName || params.item?.name || "item"}`,
  });
};

export const trackCartItemRemoved = (params = {}) => {
  trackCartEvent("remove_from_cart", {
    event_category: "Cart_Flow",
    currency: "INR",
    value: safeNumber(params.item?.unitPrice) * safeNumber(params.removedQuantity, 1),
    removal_source: safeString(params.removalSource),
    removed_quantity: safeNumber(params.removedQuantity, 1),
    ...buildCommonCartParams(params),
    items: buildSingleItemPayload({
      ...params.item,
      quantity: safeNumber(params.removedQuantity, 1),
    }),
    label: `Removed ${params.item?.productName || params.item?.name || "item"} from cart`,
  });
};

export const trackCartItemReplaced = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_item_replaced, {
    event_category: "Cart_Flow",
    old_item_id: safeString(params.oldItem?.id),
    new_item_id: safeString(params.newItem?.id),
    has_customization: Boolean(params.hasCustomization),
    ...buildCommonCartParams(params),
    items: buildSingleItemPayload(params.newItem),
    label: `Updated ${params.newItem?.productName || params.newItem?.name || "item"} customisation`,
  });
};

export const trackCartExpiryWarningShown = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_expiry_warning_shown, {
    event_category: "Cart_Flow",
    ...buildCommonCartParams(params),
    label: "Cart expiry warning shown",
  });
};

export const trackCartExpiryDismissed = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_expiry_dismissed, {
    event_category: "Cart_Flow",
    dismiss_source: safeString(params.dismissSource),
    ...buildCommonCartParams(params),
    label: `Cart expiry dismissed: ${params.dismissSource || "keep_cart"}`,
  });
};

export const trackCartCleared = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_cleared, {
    event_category: "Cart_Flow",
    clear_source: safeString(params.clearSource),
    ...buildCommonCartParams(params),
    label: `Cart cleared from ${params.clearSource || "unknown"}`,
  });
};

export const trackCustomizationChooserViewed = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_customization_chooser_viewed, {
    event_category: "Cart_Flow",
    product_id: safeString(params.productId),
    product_name: safeString(params.productName),
    customization_count: safeNumber(params.customizationCount),
    ...buildCommonCartParams(params),
    label: `Customization chooser viewed for ${params.productName || "item"}`,
  });
};

export const trackCustomizationChooserAction = (action, params = {}) => {
  const eventName =
    action === "add_new"
      ? ANALYTICS_CONFIG.events.cart_customization_add_new_clicked
      : ANALYTICS_CONFIG.events.cart_customization_edit_clicked;

  trackCartEvent(eventName, {
    event_category: "Cart_Flow",
    action,
    product_id: safeString(params.productId),
    product_name: safeString(params.productName),
    customization_count: safeNumber(params.customizationCount),
    ...buildCommonCartParams(params),
    label: `Customization chooser action: ${action}`,
  });
};

export const trackCartConversionCompleted = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_conversion_completed, {
    event_category: "Cart_Flow",
    currency: "INR",
    value: safeNumber(params.grandTotal),
    phone_captured: Boolean(params.hasPhoneNumber),
    ...buildCommonCartParams(params),
    items: buildCartItemsPayload(params.items),
    label: "Cart conversion completed — order handed to barista",
  });
};

export const trackCartAbandoned = (params = {}) => {
  trackCartEvent(ANALYTICS_CONFIG.events.cart_abandoned, {
    event_category: "Cart_Flow",
    abandon_step: safeString(params.abandonStep),
    currency: "INR",
    value: safeNumber(params.grandTotal),
    ...buildCommonCartParams(params),
    label: `Cart abandoned at ${params.abandonStep || "unknown"}`,
  });
};
